import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { agenticUrl } from "@findemdev/findem-helpers";
import { getPresentationApiUrl } from "./presentationTransport";

export interface IFileResourceSlideOption {
  slide_number: number;
  slide_name: string;
  /** Missing on legacy records means a generic template slide. */
  slide_type?: string;
}

export interface IFileResource {
  resource_id: string;
  resource_type: string;
  resource_name: string;
  owner_id: string;
  owner_account_id: string;
  file_name: string;
  s3_bucket: string;
  s3_key: string;
  size_bytes: number;
  /** Free-form user guidance for how the agent should fill/style this template.
   * Absent/empty ⇒ nothing is sent to the agent and behaviour is unchanged. */
  instructions?: string;
  /** Admin-configured slides from this template deck. */
  slide_options?: IFileResourceSlideOption[];
  /** Whether built-in/default slide actions should be shown. Custom
   * slide_options and the template file remain active when false. */
  default_slide_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * The resource_type value expected by the /file_resources endpoint.
 * Previously "ExecProjectTemplate" — now "FileResource".
 */
export const FILE_RESOURCE_TYPE = "FileResource";

/** @deprecated Use FILE_RESOURCE_TYPE. Kept for a single-release transition. */
export const RESOURCE_TYPE_EXEC_PROJECT = FILE_RESOURCE_TYPE;

/** Category identifier for the exec-project use-case. */
export const FILE_RESOURCE_CATEGORY_EXEC = "exec_project";

interface IUploadFileResourceRequest {
  joid: string;
  resource_type: string;
  /** Use-case category (e.g. "exec_project"). Appended to FormData. */
  category: string;
  formData: FormData;
}

interface IListFileResourcesRequest {
  joid: string;
  resource_type: string;
  /** Authorize Presentation template reads through this project's route. */
  projectRef?: string;
  /** When supplied, filters the list to this category. */
  category?: string;
}

interface IDeleteFileResourceRequest {
  joid: string;
  resource_id: string;
  /** Passed so invalidatesTags can key the list cache correctly. */
  resource_type: string;
}

/** PUT /file_resources/:resource_id — metadata-only update (never the file).
 * `instructions: ""` clears the saved guidance. */
interface IUpdateFileResourceRequest {
  joid: string;
  resource_id: string;
  /** Passed so invalidatesTags can key the list cache correctly. */
  resource_type: string;
  instructions?: string;
  resource_name?: string;
}

interface IDeleteFileResourceResponse {
  deleted: boolean;
  resource_id: string;
}

const baseQuery = fetchBaseQuery({
  baseUrl: `${agenticUrl}/agentic/api`,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set("accept", "application/json");
    return headers;
  },
});

export const accountResourceFilesApi = createApi({
  reducerPath: "accountResourceFilesApi",
  baseQuery,
  tagTypes: ["FileResource"],
  endpoints: (builder) => ({
    // POST /file_resources — creates a new record (no upsert).
    uploadAccountResourceFile: builder.mutation<
      IFileResource,
      IUploadFileResourceRequest
    >({
      query: ({ joid, resource_type, category, formData }) => {
        // Service is authoritative for resource_type and category in the multipart body.
        formData.set("resource_type", resource_type);
        formData.append("category", category);
        return {
          url: `/file_resources?joid=${encodeURIComponent(joid)}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "FileResource" as const, id: `${arg.joid}:${arg.resource_type}` },
      ],
    }),

    // GET /file_resources?joid=&resource_type=[&category=] — returns IFileResource[].
    getAccountResourceFile: builder.query<
      IFileResource[],
      IListFileResourcesRequest
    >({
      query: ({ joid, resource_type, category, projectRef }) => {
        const params: Record<string, string> = { joid, resource_type };
        if (category) params.category = category;
        const path = projectRef
          ? getPresentationApiUrl(projectRef, "/file_resources")
          : "/file_resources";
        return { url: `${path}?${new URLSearchParams(params).toString()}` };
      },
      providesTags: (_result, _error, { joid, resource_type }) => [
        { type: "FileResource", id: `${joid}:${resource_type}` },
      ],
    }),

    // PUT /file_resources/:resource_id?joid= — metadata only (instructions /
    // resource_name); never re-uploads the file. PUT (not PATCH) so it traverses
    // the existing edge/ACL stack, which only permits post/put/delete here.
    updateAccountResourceFile: builder.mutation<
      IFileResource,
      IUpdateFileResourceRequest
    >({
      query: ({ joid, resource_id, instructions, resource_name }) => ({
        url: `/file_resources/${encodeURIComponent(resource_id)}?joid=${encodeURIComponent(joid)}`,
        method: "PUT",
        body: {
          ...(instructions !== undefined ? { instructions } : {}),
          ...(resource_name !== undefined ? { resource_name } : {}),
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "FileResource" as const, id: `${arg.joid}:${arg.resource_type}` },
      ],
    }),

    // DELETE /file_resources/:resource_id?joid=
    deleteFileResource: builder.mutation<
      IDeleteFileResourceResponse,
      IDeleteFileResourceRequest
    >({
      query: ({ joid, resource_id }) => ({
        url: `/file_resources/${encodeURIComponent(resource_id)}?joid=${encodeURIComponent(joid)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "FileResource" as const, id: `${arg.joid}:${arg.resource_type}` },
      ],
    }),
  }),
});

export const {
  useUploadAccountResourceFileMutation,
  useGetAccountResourceFileQuery,
  useLazyGetAccountResourceFileQuery,
  useUpdateAccountResourceFileMutation,
  useDeleteFileResourceMutation,
} = accountResourceFilesApi;
